import React, { useState, useEffect } from 'react';
import api from '../api';
import logger from '../utils/logger';
import './SystemStatus.css';

const SystemStatus = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [frontendBuildInfo, setFrontendBuildInfo] = useState(null);
  const [storageStats, setStorageStats] = useState(null);
  const [storageLoading, setStorageLoading] = useState(false);
  const [showAllCollections, setShowAllCollections] = useState(false);

  // Check frontend Firebase configuration
  const frontendFirebaseConfigured = Boolean(
    process.env.REACT_APP_FIREBASE_API_KEY &&
    process.env.REACT_APP_FIREBASE_PROJECT_ID &&
    process.env.REACT_APP_FIREBASE_VAPID_KEY
  );

  useEffect(() => {
    loadSystemStatus();
    loadFrontendBuildInfo();
    loadStorageStats();
    // Refresh every 30 seconds
    const interval = setInterval(loadSystemStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadSystemStatus = async () => {
    try {
      setLoading(true);
      const response = await api.get('/system/health');
      setStatus(response.data);
      setError(null);
    } catch (err) {
      logger.error('Error loading system status:', err);
      setError('Failed to load system status');
    } finally {
      setLoading(false);
    }
  };

  const loadFrontendBuildInfo = async () => {
    try {
      const response = await fetch('/build-info.json');
      const data = await response.json();
      setFrontendBuildInfo(data);
    } catch (err) {
      logger.error('Error loading frontend build info:', err);
      // Fallback to default values
      setFrontendBuildInfo({
        buildTime: 'unknown',
        buildDate: 'unknown',
        version: '1.0.0'
      });
    }
  };

  const loadStorageStats = async () => {
    try {
      setStorageLoading(true);
      const response = await api.get('/system/health/storage');
      setStorageStats(response.data);
    } catch (err) {
      logger.error('Error loading storage stats:', err);
    } finally {
      setStorageLoading(false);
    }
  };

  const getStatusIcon = (isHealthy) => {
    return isHealthy ? '✅' : '❌';
  };

  const getEnvironmentBadge = (env) => {
    const labels = {
      local: 'Local',
      dev: 'Development',
      stage: 'Staging',
      production: 'Production'
    };
    const key = labels[env] ? env : 'local';
    return <span className={`env-badge env-${key}`}>{labels[key]}</span>;
  };

  if (loading && !status) {
    return (
      <div className="system-status-container">
        <div className="loading-spinner">Loading system status...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="system-status-container">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="system-status-container">
      <div className="status-header">
        <h3>🔧 Backend Services Status</h3>
        <button 
          className="btn-refresh" 
          onClick={loadSystemStatus}
          disabled={loading}
        >
          🔄 Refresh
        </button>
      </div>

      {/* Environment Info */}
      <div className="status-section">
        <div className="status-item">
          <span className="status-label">🌍 Environment:</span>
          <span className="status-value">
            {getEnvironmentBadge(status?.environment)}
          </span>
        </div>
        <div className="status-item">
          <span className="status-label">🏢 Backend URL:</span>
          <span className="status-value ss-dim" >
            {status?.backend_url || 'N/A'}
          </span>
        </div>
      </div>

      {/* Service Status */}
      <div className="status-section">
        <h4 className="ss-h4" >Services</h4>
        
        {/* MongoDB */}
        <div className="service-status-item">
          <div className="service-info">
            <span className="service-icon">🗄️</span>
            <div className="service-details">
              <span className="service-name">MongoDB</span>
              <span className="service-description">
                {status?.services?.mongodb?.details || 'Database'}
              </span>
            </div>
          </div>
          <div className="service-status">
            <span className={status?.services?.mongodb?.healthy ? 'ss-ok' : 'ss-bad'}>
              {getStatusIcon(status?.services?.mongodb?.healthy)} 
              {status?.services?.mongodb?.healthy ? 'Connected' : 'Disconnected'}
            </span>
          </div>
        </div>

        {/* Redis */}
        <div className="service-status-item">
          <div className="service-info">
            <span className="service-icon">🔴</span>
            <div className="service-details">
              <span className="service-name">Redis</span>
              <span className="service-description">
                {status?.services?.redis?.details || 'Cache & Sessions'}
              </span>
            </div>
          </div>
          <div className="service-status">
            <span className={status?.services?.redis?.healthy ? 'ss-ok' : 'ss-bad'}>
              {getStatusIcon(status?.services?.redis?.healthy)} 
              {status?.services?.redis?.healthy ? 'Connected' : 'Disconnected'}
            </span>
          </div>
        </div>

        {/* Firebase Backend */}
        <div className="service-status-item">
          <div className="service-info">
            <span className="service-icon">🔥</span>
            <div className="service-details">
              <span className="service-name">Firebase (Backend)</span>
              <span className="service-description">
                Server-side Push Notifications
              </span>
            </div>
          </div>
          <div className="service-status">
            <span className={status?.services?.firebase?.configured ? 'ss-ok' : 'ss-warn'}>
              {status?.services?.firebase?.configured ? '✅ Configured' : '⚠️ Not Configured'}
            </span>
          </div>
        </div>

        {/* Firebase Frontend */}
        <div className="service-status-item">
          <div className="service-info">
            <span className="service-icon">🌐</span>
            <div className="service-details">
              <span className="service-name">Firebase (Frontend)</span>
              <span className="service-description">
                Browser Push Notifications
              </span>
            </div>
          </div>
          <div className="service-status">
            <span className={frontendFirebaseConfigured ? 'ss-ok' : 'ss-bad'}>
              {frontendFirebaseConfigured ? '✅ Configured' : '❌ Not Configured'}
            </span>
          </div>
        </div>

        {/* Storage */}
        <div className="service-status-item">
          <div className="service-info">
            <span className="service-icon">📦</span>
            <div className="service-details">
              <span className="service-name">Storage</span>
              <span className="service-description">
                {status?.services?.storage?.type === 'gcs' ? 'Google Cloud Storage' : 'Local File System'}
              </span>
            </div>
          </div>
          <div className="service-status">
            <span 
              
              className="ss-ok" 
            >
              ✅ {status?.services?.storage?.type === 'gcs' ? 'GCS' : 'Local'}
            </span>
          </div>
        </div>
      </div>

      {/* System Info */}
      <div className="status-section">
        <h4 className="ss-h4" >System Info</h4>
        <div className="info-grid">
          <div className="info-item">
            <span className="info-label">Uptime:</span>
            <span className="info-value">{status?.uptime || 'N/A'}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Version:</span>
            <span className="info-value">{status?.version || '1.0.0'}</span>
          </div>
        </div>
      </div>

      {/* MongoDB Storage Stats */}
      <div className="status-section">
        <div className="ss-row-between ss-mb-12" >
          <h4 className="ss-h4m" >💾 MongoDB Storage (Free Tier: 512 MB)</h4>
          <button 
            className="btn-refresh ss-btn-sm" 
            onClick={loadStorageStats}
            disabled={storageLoading}
            
          >
            🔄 {storageLoading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
        
        {storageStats ? (
          <>
            {/* Usage Progress Bar */}
            <div className="ss-mb-16" >
              <div className="ss-row-between ss-mb-6" >
                <span className="ss-label" >
                  {storageStats.totalSizeFormatted} / {storageStats.freeTierLimitFormatted}
                </span>
                <span className={`ss-label ss-st-${storageStats.status || 'healthy'}`}>
                  {storageStats.usagePercent}% used
                </span>
              </div>
              <div className="ss-bar-track" >
                <div
                  className={`ss-bar-fill ss-st-${storageStats.status || 'healthy'}`}
                  style={{ width: `${Math.min(storageStats.usagePercent, 100)}%` }}
                />
              </div>
              <div className="ss-meta" >
                <span>Data: {storageStats.dataSizeFormatted}</span>
                <span>Indexes: {storageStats.indexSizeFormatted}</span>
                <span>Documents: {storageStats.totalDocuments?.toLocaleString()}</span>
              </div>
            </div>

            {/* Collections Table */}
            <div className="ss-table-wrap" >
              <table className="storage-table" >
                <thead>
                  <tr >
                    <th >Collection</th>
                    <th className="num" >Docs</th>
                    <th className="num" >Size</th>
                    <th className="num" >%</th>
                  </tr>
                </thead>
                <tbody>
                  {(showAllCollections ? storageStats.collections : storageStats.collections?.slice(0, 8))?.map((coll, idx) => (
                    <tr key={coll.name}>
                      <td >
                        <span className="ss-mono" >{coll.name}</span>
                      </td>
                      <td className="num" >
                        {coll.documents?.toLocaleString()}
                      </td>
                      <td className="num ss-fw500" >
                        {coll.totalSizeFormatted}
                      </td>
                      <td className="num" >
                        <span className={`pct-chip ${coll.percentOfTotal > 20 ? 'hot' : ''}`}>
                          {coll.percentOfTotal}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {storageStats.collections?.length > 8 && (
                <button
                  onClick={() => setShowAllCollections(!showAllCollections)}
                  className="ss-table-more" 
                >
                  {showAllCollections ? '▲ Show Less' : `▼ Show All ${storageStats.collections.length} Collections`}
                </button>
              )}
            </div>

            {/* Recommendations */}
            {storageStats.recommendations?.length > 0 && (
              <div className="ss-mt-12" >
                <h5 className="ss-h5" >
                  💡 Optimization Recommendations
                </h5>
                {storageStats.recommendations.slice(0, 3).map((rec, idx) => (
                  <div key={idx} className={`rec-item pri-${rec.priority || 'low'}`}>
                    <div className="ss-row-between ss-mb-4" >
                      <span className="ss-fw600" >{rec.collection}</span>
                      <span className={`rec-badge pri-${rec.priority || 'low'}`}>
                        {rec.priority}
                      </span>
                    </div>
                    <div className="ss-muted ss-mb-4" >{rec.issue}</div>
                    <div className="ss-fw500" >→ {rec.action}</div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : storageLoading ? (
          <div className="ss-empty" >
            Loading storage stats...
          </div>
        ) : (
          <div className="ss-empty" >
            Failed to load storage stats
          </div>
        )}
      </div>

      {/* Build Information */}
      <div className="status-section">
        <h4 className="ss-h4" >📦 Build Information</h4>
        
        {/* Backend Build Info */}
        <div className="build-info-block ss-mb-12" >
          <h5 className="ss-h5 ss-h5-lg" >
            <span>⚙️</span> Backend
          </h5>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Build Date:</span>
              <span className="info-value" >
                {status?.buildInfo?.buildDate || 'unknown'}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">Version:</span>
              <span className="info-value">
                {status?.buildInfo?.version || '1.0.0'}
              </span>
            </div>
          </div>
        </div>

        {/* Frontend Build Info */}
        <div className="build-info-block" >
          <h5 className="ss-h5 ss-h5-lg" >
            <span>🌐</span> Frontend
          </h5>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Build Date:</span>
              <span className="info-value" >
                {frontendBuildInfo?.buildDate || 'unknown'}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">Version:</span>
              <span className="info-value">
                {frontendBuildInfo?.version || '1.0.0'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Last Updated */}
      <div className="status-footer">
        <span className="ss-dim-12" >
          Last updated: {new Date().toLocaleTimeString()}
        </span>
      </div>
    </div>
  );
};

export default SystemStatus;
